import { useQuery } from '@tanstack/react-query';

import { supabase } from './supabase.ts';

/**
 * Names for the accounts in the staff token.
 *
 * The JWT carries ids and roles but no names — the hook keeps it small — so
 * anything that shows the user their workspaces has to look the names up.
 * `accounts_read` admits members and owners, which is exactly the set the
 * token lists, so the read returns every row asked for.
 */

export interface AccountSummary {
  id: string;
  name: string;
  isPersonal: boolean;
}

export async function listAccountSummaries(ids: string[]): Promise<AccountSummary[]> {
  if (ids.length === 0) return [];

  const { data, error } = await supabase
    .from('accounts')
    .select('id, name, is_personal_account')
    .in('id', ids);

  if (error) throw error;

  return (data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    isPersonal: row.is_personal_account,
  }));
}

export const ACCOUNT_SUMMARIES_KEY = 'account-summaries';

/** The summaries keyed by id; empty until loaded, so callers fall back to the id. */
export function useAccountSummaries(ids: string[]): Map<string, AccountSummary> {
  const query = useQuery({
    queryKey: [ACCOUNT_SUMMARIES_KEY, ...ids],
    queryFn: () => listAccountSummaries(ids),
    staleTime: 5 * 60 * 1000,
  });

  return new Map((query.data ?? []).map((account) => [account.id, account]));
}
